import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import './WhatsProfile.css'
import {
  Badge,
  Button,
  CloseButton,
  Dropdown,
  Form,
  Modal,
  Toast,
  ToastContainer
} from 'react-bootstrap'
import { connect } from 'react-redux'
import config from '../../../config'
import axios from 'axios'
import { EllipsisVertical, Users } from 'lucide-react'

function WhatsProfile({ authToken }) {
  const navigate = useNavigate()
  const [searchResult, setSearchResult] = useState([])
  const [allUsers, setAllUsers] = useState([]) // New state to store all users
  const [search, setSearch] = useState('')
  const [groupChatName, setGroupChatName] = useState('')
  const [renameLoading, setRenameLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [isScrolled, setIsScrolled] = useState(false)
  const [fetchAgain, setFetchAgain] = useState(false)
  const [loggedUser, setLoggedUser] = useState()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastVariant, setToastVariant] = useState('danger')
  const [show, setShow] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [currentGroup, setCurrentGroup] = useState(null)
  const [loadingUsers, setLoadingUsers] = useState(false)

  const handleClose = () => {
    setShow(false)
    setSearch('')
    setSearchResult([])
  }

  // Modified to fetch all users when opening the modal
  const handleShow = () => {
    setShow(true)
    fetchAllUsers()
  }

  const handleCloseRename = () => {
    setShowModal(false)
    setGroupChatName('')
  }
  const handleShowRename = () => setShowModal(true)

  const location = useLocation()
  const { user, group } = location.state || {}

  // toast message handler
  const displayToast = (message, variant = 'danger') => {
    setToastMessage(message)
    setToastVariant(variant)
    setShowToast(true)
    // Auto hide toast after 3 seconds
    setTimeout(() => setShowToast(false), 2000)
  }

  // phoneNumber handle
  const formatPhoneNumber = phoneNumber => {
    if (!phoneNumber) return ''

    // Handle phone numbers with country code
    if (phoneNumber.startsWith('91') && phoneNumber.length >= 12) {
      return `+${phoneNumber.substring(0, 2)} ${phoneNumber.substring(
        2,
        7
      )} ${phoneNumber.substring(7)}`
    }

    // For other formats or shorter numbers, add basic formatting
    if (phoneNumber.length === 10) {
      return `${phoneNumber.substring(0, 5)}-${phoneNumber.substring(5)}`
    }
    return phoneNumber
  }

  // New function to fetch all users
  const fetchAllUsers = async () => {
    setLoadingUsers(true)
    try {
      const head = { headers: { Authorization: `Bearer ${authToken}` } }
      const { data } = await axios.get(`${config.devapi}/user`, head)
      setAllUsers(data)
      setSearchResult(data) // Initialize search results with all users
    } catch (error) {
      console.error('Failed to load users', error)
      displayToast('Failed to load users', 'danger')
    } finally {
      setLoadingUsers(false)
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 15) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'))
    if (userInfo) {
      setLoggedUser(userInfo)
    }
  }, [])

  // Modified search effect to filter from all users instead of making API calls
  useEffect(() => {
    if (!search.trim()) {
      setSearchResult(allUsers)
      return
    }

    const filteredUsers = allUsers.filter(
      user =>
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
    )
    setSearchResult(filteredUsers)
  }, [search, allUsers])

  useEffect(() => {
    if (group) {
      setCurrentGroup(group)
    }
  }, [group])

  // fetching the updated details in group
  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (!currentGroup?._id) {
        console.warn('No valid group ID to fetch details')
        return
      }

      try {
        const head = {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
        const { data } = await axios.get(
          `${config.devapi}chat/${currentGroup._id}`,
          head
        )
        console.log('Fetched Group Details:', data)
        setCurrentGroup(data) // Update with fresh data
      } catch (error) {
        console.error('Failed to fetch group details:', error)
        displayToast('Failed to update group information', 'danger')
      }
    }

    if (fetchAgain && currentGroup?._id) {
      fetchGroupDetails()
      setFetchAgain(false) // Reset to prevent infinite loop
    }
  }, [fetchAgain, currentGroup?._id, authToken])

  // selecting users in modal
  const handleAddUser = user1 => {
    if (selectedUsers.find(u => u._id === user1._id)) {
      displayToast('User already selected!', 'warning')
      return
    }

    // Check if user is already in the group using currentGroup instead of group
    if (currentGroup?.users?.find(u => u._id === user1._id)) {
      displayToast('User already exists in the group!', 'warning')
      return
    }

    setSelectedUsers([...selectedUsers, user1])
  }

  // Remove selected members from the list in modal
  const handleDelete = user1 => {
    setSelectedUsers(selectedUsers.filter(u => u._id !== user1._id))
  }

  // add users to the group
  const handleSubmitUsers = async () => {
    if (!currentGroup?._id) {
      displayToast('No valid group selected!', 'warning')
      return
    }
    if (selectedUsers.length === 0) {
      displayToast('No users selected!', 'warning')
      return
    }

    try {
      const head = {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }

      const { data } = await axios.put(
        `${config.devapi}/chat/groupadd`,
        {
          chatId: currentGroup._id,
          userIds: selectedUsers.map(u => u._id)
        },
        head
      )

      console.log('Updated Group after adding users:', data)
      setCurrentGroup(data)
      setSelectedUsers([])
      setSearch('')
      setSearchResult([])
      setShow(false)
      displayToast('Users added successfully!', 'success')
      setFetchAgain(true)
    } catch (error) {
      console.error('Error adding users:', error)
      const errorMessage = error.response?.data?.message || 'Error adding users'
      displayToast(errorMessage, 'danger')
    }
  }

  //group chat rename
  const handleRename = async () => {
    if (!groupChatName?.trim()) {
      displayToast('Please enter a group name', 'warning')
      return
    }

    try {
      setRenameLoading(true)
      const head = {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }

      const { data } = await axios.put(
        `${config.devapi}/chat/rename`,
        {
          chatId: currentGroup._id,
          chatName: groupChatName.trim()
        },
        head
      )

      console.log('Updated Group after rename:', data)
      setCurrentGroup(data)
      setShowModal(false)
      setGroupChatName('')
      displayToast('Group renamed successfully!', 'success')
      setFetchAgain(true)
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Error renaming group'
      displayToast(errorMessage, 'danger')
    } finally {
      setRenameLoading(false)
    }
  }

  // make member a group admin
  const makeAdmin = async memberId => {
    // Check if current user is the group admin - use currentGroup instead of group
    const isCurrentUserAdmin =
      currentGroup?.groupAdmin?._id === loggedUser?._id ||
      (loggedUser?.isAdmin &&
        currentGroup?.users?.some(u => u._id === loggedUser._id))

    if (!isCurrentUserAdmin) {
      displayToast('Only group admin can make others admin', 'warning')
      return
    }

    // Prevent making the same user an admin if they're already one
    const targetUser = currentGroup.users.find(user => user._id === memberId)
    if (targetUser?.isAdmin) {
      displayToast('User is already an admin', 'warning')
      return
    }

    try {
      const head = {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }

      const { data } = await axios.post(
        `${config.devapi}/chat/makeadmin`,
        {
          chatId: currentGroup._id,
          userId: memberId
        },
        head
      )

      console.log('Updated Group after admin:', data)
      setCurrentGroup(data)
      displayToast('User is now an admin', 'success')
      setFetchAgain(true)
    } catch (error) {
      console.error('Error making user admin:', error)
      displayToast('Failed to make user an admin', 'danger')
    }
  }

  // remove user from group by admin
  const removeUser = async memberId => {
    // Check if current user is the group admin - use currentGroup instead of group
    const isCurrentUserAdmin =
      currentGroup?.groupAdmin?._id === loggedUser?._id ||
      (loggedUser?.isAdmin &&
        currentGroup?.users?.some(u => u._id === loggedUser._id))

    if (!isCurrentUserAdmin) {
      displayToast('Only group admin can remove members', 'warning')
      return
    }

    // Prevent removing yourself through this function
    if (memberId === loggedUser._id) {
      displayToast('Use the leave button to leave the group', 'warning')
      return
    }

    try {
      const head = {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }

      const { data } = await axios.put(
        `${config.devapi}/chat/groupremove`,
        {
          chatId: currentGroup._id,
          userId: memberId
        },
        head
      )

      // If the removed user is in selected users, remove them from there too
      if (selectedUsers.some(user => user._id === memberId)) {
        setSelectedUsers(selectedUsers.filter(user => user._id !== memberId))
      }

      console.log('Updated Group after remove:', data)
      setCurrentGroup(data)
      displayToast('User removed from group', 'success')
      setFetchAgain(true)
    } catch (error) {
      console.error('Error removing user:', error)
      const errorMessage =
        error.response?.data?.message || 'Failed to remove user'
      displayToast(errorMessage, 'danger')
    }
  }

  // function for leaving the group
  const handleLeave = async () => {
    // Ensure current group and user are valid
    if (!currentGroup?._id || !loggedUser?._id) {
      displayToast('No valid group or user selected!', 'warning')
      return
    }

    try {
      const head = {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      }

      const { data } = await axios.put(
        `${config.devapi}/chat/groupremove`,
        {
          chatId: currentGroup._id,
          userId: loggedUser._id
        },
        head
      )

      // Update state with new group data
      console.log('Left group successfully')
      setCurrentGroup(data)
      displayToast('You have left the group successfully!', 'success')

      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/dashboard')
      }, 1000)

      // Refresh group list
      setFetchAgain(true)
    } catch (error) {
      // Improved error handling
      console.error('Error leaving group:', error)
      const errorMessage =
        error.response?.data?.message || 'Error leaving group'
      displayToast(errorMessage, 'danger')
    }
  }

  return (
    <>
      {/* toast message  */}
      <ToastContainer
        className="p-3"
        position="middle-center"
        style={{ zIndex: 1060 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          bg={toastVariant}
          delay={2000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      {/* App Header */}
      <div
        className={`appHeader bg-primary ${
          isScrolled ? 'is-active' : ''
        } w-100`}
        style={{
          position: 'fixed',
          top: '0',
          zIndex: 1000, // Ensure it stays above other elements
          backgroundColor: 'white', // Avoid transparency issues
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' // Optional: adds shadow for visibility
        }}
      >
        <div className="left">
          <a className="headerButton goBack" onClick={() => navigate(-1)}>
            <ion-icon name="arrow-back-outline"></ion-icon>
          </a>
        </div>
        <div className="avatar">
          {currentGroup || group ? (
            <>
              <div
                className="logo imaged rounded"
                style={{
                  backgroundColor: '#e0e0e0',
                  height: '120px'
                }}
              >
                <ion-icon
                  name="people-sharp"
                  style={{ color: '#b1b0b0', width: '120px', height: '100px' }}
                ></ion-icon>
              </div>
              {/* <img
                src={
                  currentGroup?.groupAdmin?.pic ||
                  group?.groupAdmin?.pic ||
                  user?.pic
                }
                className="logo imaged rounded"
                alt="Profile"
                width="65"
              /> */}
            </>
          ) : (
            <img
              src={user?.pic}
              className="logo imaged rounded"
              alt="Profile"
              width="65"
            />
          )}
          <div
            className={`pageTitle ${isScrolled ? 'visible' : 'hidden'}`}
            style={{ right: '39rem' }}
          >
            {currentGroup?.chatName || group?.chatName || user?.name}
          </div>
        </div>
        <div className="right">
          <Dropdown className="me-1">
            <Dropdown.Toggle variant="light" id="dropdown-basic">
              <EllipsisVertical />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {currentGroup || group ? (
                <>
                  <Dropdown.Item>
                    <Link to="/settings">Settings</Link>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleShowRename}>
                    Change Group Name
                  </Dropdown.Item>
                  {/* modal to change group name */}
                  <Modal show={showModal} onHide={handleCloseRename} fullscreen>
                    <Modal.Header closeButton>
                      <Modal.Title>Change Group Name</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <Form>
                        <Form.Group className="mb-2">
                          <Form.Control
                            type="text"
                            placeholder="Change Group Name"
                            value={groupChatName}
                            onChange={e => setGroupChatName(e.target.value)}
                          />
                        </Form.Group>
                      </Form>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                      <Button
                        className="mx-2"
                        variant="secondary"
                        onClick={handleCloseRename}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="mx-2"
                        variant="success"
                        disabled={renameLoading}
                        onClick={handleRename}
                      >
                        {renameLoading ? 'Renaming...' : 'Update'}
                      </Button>
                    </Modal.Footer>
                  </Modal>
                  <Dropdown.Item onClick={handleShow}>
                    Add members
                  </Dropdown.Item>
                </>
              ) : (
                <>
                  <Dropdown.Item>
                    <Link to="/settings">Settings</Link>
                  </Dropdown.Item>
                  <Dropdown.Item>Share</Dropdown.Item>
                  <Dropdown.Item>
                    <Link to="/community">Community</Link>
                  </Dropdown.Item>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      {/* App Header */}

      {/* App Capsule */}
      <div id="appCapsule">
        {/* name of group/user  */}
        <div
          className={`section mt-2 ${isScrolled ? 'is-active' : ''}`}
          style={{ height: '155px' }}
        >
          <div className="profile-head">
            <div className="in in-pro mt-5">
              <div className="d-flex flex-column align-items-center lh-sm">
                <h3 className={`name ${isScrolled ? 'hidden' : 'visible'}`}>
                  {currentGroup?.chatName || group?.chatName || user?.name}
                </h3>
                {/* Display phone and email for individual user chats */}
                {!currentGroup && !group && user && (
                  <div className="d-flex flex-column align-items-center ">
                    {user.phone && (
                      <div className="phone " style={{ fontSize: '14px' }}>
                        {formatPhoneNumber(user.phone)}
                      </div>
                    )}
                    {user.email && (
                      <div className="email" style={{ fontSize: '14px' }}>
                        {user.email}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Group or User Information */}
        <div
          className={`section ${currentGroup || group ? 'mt-1' : 'mt-3'} mb-2`}
        >
          <div className="profile-info">
            <div className="bio">
              Hi!
              {currentGroup || group
                ? ` This is a group chat: ${
                    currentGroup?.chatName || group?.chatName
                  }`
                : ' This is a user profile'}
            </div>
            <div className="link">
              <p>
                {currentGroup || group
                  ? `Created on: ${new Date(
                      (currentGroup || group).createdAt
                    ).toDateString()}`
                  : 'Joined recently'}
              </p>
            </div>
          </div>
        </div>

        {/* Group Members (Only for Group Chats) */}
        {(currentGroup || group) && (
          <div className="section mt-1 px-0">
            {console.log(currentGroup || group)}
            <h4 className="px-3">
              Members ({(currentGroup?.users || group?.users)?.length || 0})
            </h4>
            <ul className="listview image-listview">
              <li
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'rgb(244 244 244)',
                  height: '50px'
                }}
                onClick={handleShow}
              >
                <label className="px-2 py-3 fs-5 d-flex align-items-center">
                  <ion-icon
                    name="person-add-outline"
                    style={{ marginRight: '7px' }}
                  ></ion-icon>
                  Add members
                </label>
              </li>
              {/* modal to add members */}
              <Modal show={show} onHide={handleClose} fullscreen>
                <Modal.Header closeButton>
                  <Modal.Title className="d-flex align-items-center">
                    Add members
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Search Users eg: John, Piyush, Jane"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                    </Form.Group>
                    <div className="mb-3">
                      {selectedUsers.map(user => (
                        <Badge
                          pill
                          bg="primary"
                          className="me-2 p-2 mb-1"
                          key={user._id}
                        >
                          {user.name}
                          <CloseButton
                            variant="black"
                            onClick={() => handleDelete(user)}
                            className="ms-1"
                          />
                        </Badge>
                      ))}
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {loadingUsers ? (
                        <div className="text-center py-3">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : searchResult.length > 0 ? (
                        searchResult.map(user => (
                          <div
                            key={user._id}
                            className="d-flex align-items-center my-2 px-2 border-bottom pb-2"
                            onClick={() => handleAddUser(user)}
                            style={{ cursor: 'pointer' }}
                          >
                            <img
                              src={user.pic}
                              alt="user-icon"
                              className="rounded-circle me-2"
                              width="36"
                              height="36"
                            />
                            <div className="d-flex flex-column">
                              <label>{user.name}</label>
                              <label>
                                <b>Mobile No: </b>
                                {formatPhoneNumber(user.phone)}
                              </label>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-3">No users found</div>
                      )}
                    </div>
                  </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                  <Button
                    className="mx-2"
                    variant="secondary"
                    onClick={handleClose}
                  >
                    <ion-icon
                      name="close-outline"
                      style={{ marginRight: '0px' }}
                    ></ion-icon>
                  </Button>
                  <Button
                    className="mx-2"
                    variant="success"
                    onClick={handleSubmitUsers}
                    disabled={selectedUsers.length === 0}
                  >
                    <ion-icon
                      name="checkmark-outline"
                      style={{ marginRight: '0px' }}
                    ></ion-icon>
                  </Button>
                </Modal.Footer>
              </Modal>
              {(currentGroup?.users || group?.users)?.map(member => (
                <li key={member?._id}>
                  <div className="item inn d-flex align-items-center justify-content-between">
                    {/* Avatar */}
                    <div className="d-flex align-items-center">
                      <div className="avatar">
                        {member?.type === 'bot' ? (
                          <>
                            <div
                              className="image d-flex align-items-center justify-content-center col-dry m-0 bot-pic"
                              style={{
                                backgroundColor: '#e0e0e0',
                                maxHeight: '32px',
                                padding: '1px'
                              }}
                            >
                              <ion-icon
                                name="logo-ionitron"
                                style={{ fontSize: '32px', color: '#bdbdbd' }} // Optional styling
                              ></ion-icon>
                              <div
                                className="type-img1"
                                style={{
                                  backgroundColor: '#000'
                                }}
                              >
                                <div class="type-img-icon">
                                  <ion-icon
                                    name="logo-ionitron"
                                    style={{
                                      color: '#fff',
                                      width: '11px'
                                    }}
                                  ></ion-icon>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <img
                              src={member?.pic || 'default-profile.jpg'}
                              alt={member?.name}
                              className="imaged w32 rounded"
                            />
                            <div
                              className="type-img1"
                              style={{ backgroundColor: '#000', width: '16px' }}
                            >
                              <div class="type-img-icon">
                                <ion-icon
                                  name="person-sharp"
                                  style={{
                                    color: '#fff',
                                    width: '11px'
                                  }}
                                ></ion-icon>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Member Info */}
                      <div
                        className="in ms-2 d-flex flex-column align-items-start"
                        style={{ fontSize: '14px' }}
                      >
                        <div className="d-flex align-items-center">
                          {member?.name}
                          {/* Check in both currentGroup and group for admin status */}
                          {(member?._id === currentGroup?.groupAdmin?._id ||
                            member?._id === group?.groupAdmin?._id ||
                            member?.isAdmin) && (
                            <span
                              className="badge admin ms-md-3"
                              style={{
                                backgroundColor: 'lightgreen',
                                color: 'black',
                                fontSize: '10px',
                                padding: '3px 6px',
                                borderRadius: '5px'
                              }}
                            >
                              Admin
                            </span>
                          )}
                        </div>
                        <h5 className="subtext" style={{ fontSize: '11px' }}>
                          {member?.type === 'bot'
                            ? member?.display || 'No email available'
                            : member?.phone
                            ? formatPhoneNumber(member.phone)
                            : member?.email || 'No contact info'}
                        </h5>
                      </div>
                    </div>

                    {/* Admin Controls - check against currentGroup, not just group */}
                    {(loggedUser?._id === currentGroup?.groupAdmin?._id ||
                      loggedUser?._id === group?.groupAdmin?._id ||
                      (loggedUser?.isAdmin && (currentGroup || group))) &&
                      member?._id !== loggedUser?._id && (
                        <div className="d-flex">
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => makeAdmin(member._id)}
                          >
                            Make Admin
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => removeUser(member._id)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Other Options */}
        <div
          className="tab-pane fade active show"
          id="settings"
          role="tabpanel"
        >
          {/* <div className="media-links inn mt-2">
            <div style={{ padding: '0 16px', paddingTop: '15px' }}>
              Media,links,and docs
            </div>
            <ul
              className="listview image-listview media"
              style={{ display: 'flex', overflowX: 'auto' }}
            >
              <li>
                <div className="item">
                  <div className="imageWrapper">
                    <img
                      src="assets/img/avatar1.jpg"
                      alt="image"
                      className="imaged w120"
                    />
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="imageWrapper">
                    <img
                      src="assets/img/avatar1.jpg"
                      alt="image"
                      className="imaged w120"
                    />
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="imageWrapper">
                    <img
                      src="assets/img/avatar1.jpg"
                      alt="image"
                      className="imaged w120"
                    />
                  </div>
                </div>
              </li>
              <li>
                <div className="item">
                  <div className="imageWrapper">
                    <img
                      src="assets/img/avatar1.jpg"
                      alt="image"
                      className="imaged w120"
                    />
                  </div>
                </div>
              </li>
            </ul>
          </div>
          &nbsp;
          <ul className="listview image-listview">
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="notifications-outline"
                    role="img"
                    className="md hydrated"
                    aria-label="notifications-outline"
                  ></ion-icon>
                </div>
                <div className="in">
                  <div>Notifications</div>
                </div>
              </div>
            </li>
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="image-outline"
                    role="img"
                    className="md hydrated"
                    aria-label="image-outline"
                  ></ion-icon>
                </div>
                <div className="in">
                  <div>Media visibility</div>
                </div>
              </div>
            </li>
          </ul>
          &nbsp;
          <ul className="listview image-listview">
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="lock-closed-outline"
                    role="img"
                    className="md hydrated"
                    aria-label="lock-closed-outline"
                  ></ion-icon>
                </div>
                <div className="in">
                  <div>
                    Encryption
                    <footer>
                      Messages and calls are end-to-end encrypted. Tap to verify
                    </footer>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="timer-outline"
                    role="img"
                    className="md hydrated"
                    aria-label="timer-outline"
                  ></ion-icon>
                </div>
                <div className="in">
                  <div>
                    Disappearing messages
                    <footer>Off</footer>
                  </div>
                </div>
              </div>
            </li>
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="videocam-outline"
                    role="img"
                    className="md hydrated"
                    aria-label="videocam outline"
                  ></ion-icon>
                </div>
                <div className="in">
                  <div>
                    Chat lock
                    <footer>Lock and hide this chat on this device</footer>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="SwitchCheckDefault1"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="SwitchCheckDefault1"
                    ></label>
                  </div>
                </div>
              </div>
            </li>
          </ul> */}
          &nbsp;
          <ul className="listview image-listview">
            <li>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="ban-outline"
                    style={{ color: '#d64233' }}
                  ></ion-icon>
                </div>
                <div className="in">
                  <div className="text-danger">Block</div>
                </div>
              </div>
            </li>
            <li className={!(currentGroup || group) && `mb-3`}>
              <div className="item inn">
                <div className="icon-box ">
                  <ion-icon
                    name="thumbs-down-sharp"
                    role="img"
                    className="md hydrated"
                    aria-label="thumbs-down-sharp"
                    style={{ color: '#aa3d32' }}
                  ></ion-icon>
                </div>
                <div className="in">
                  <div className="text-danger">Report</div>
                </div>
              </div>
            </li>
            {(currentGroup || group) && (
              <li className="mb-3" onClick={handleLeave}>
                <div className="item inn" style={{ cursor: 'pointer' }}>
                  <div className="icon-box ">
                    <ion-icon
                      name="exit-outline"
                      role="img"
                      className="md hydrated"
                      aria-label="exit-outline"
                      style={{ color: '#aa3d32' }}
                    ></ion-icon>
                  </div>
                  <div className="in">
                    <div className="text-danger">Leave Group</div>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
      {/* App Capsule */}
    </>
  )
}

const mapStateToProps = ({ auth }) => {
  const { authToken } = auth

  return {
    authToken
  }
}

export default connect(mapStateToProps)(WhatsProfile)
